// Client-side payment-method display + bucket model for the manage (delta) flow.
//
// Mirrors api/_utils/paymentMethods.js (METHOD_RULES) and the booking app's
// src/config/paymentMethodLabels.js. The frontend can't import the server util,
// so the three small tables below are deliberate mirrors — keep them in sync
// with METHOD_RULES whenever a method is added or its capabilities change.

// Currencies in which MobilePay can be offered. MIRROR of METHOD_RULES.mobilepay.currencies.
export const MOBILEPAY_CURRENCIES = ['dkk', 'nok', 'sek', 'eur']

// Methods that can be paired with setup_future_usage='off_session'.
// MIRROR of the methods with allowsOffSession === true in METHOD_RULES.
const OFF_SESSION_CAPABLE = new Set(['card'])

// bucket: which top-level option a method belongs under. 'card'/'bank' both live
// in the stripe_main bucket; 'mobilepay' gets its own (slow wallet probe → lazy).
const PAYMENT_METHOD_DISPLAY = {
  card:        { bucket: 'card',      brand: 'Visa, Mastercard, AMEX' },
  link:        { bucket: 'card',      brand: 'Link' },
  pay_by_bank: { bucket: 'bank',      brand: 'Pay by Bank' },
  eps:         { bucket: 'bank',      brand: 'EPS' },
  ideal:       { bucket: 'bank',      brand: 'iDEAL' },
  bancontact:  { bucket: 'bank',      brand: 'Bancontact' },
  sepa_debit:  { bucket: 'bank',      brand: 'SEPA' },
  klarna:      { bucket: 'bank',      brand: 'Klarna' },
  mobilepay:   { bucket: 'mobilepay', brand: 'MobilePay' },
}

/**
 * Resolve the label key + brand subtitle for the stripe_main bucket given its
 * resolved method list. Only card/bank methods contribute — anything else
 * (e.g. mobilepay) is ignored so its brand never leaks into the main subtitle.
 *
 * @param {string[]} methods
 * @returns {{ labelKey: string, brandSubtitle: string }}
 */
export function getCardOptionLabel(methods = []) {
  const present = new Set(
    methods.map(m => PAYMENT_METHOD_DISPLAY[m]?.bucket).filter(b => b === 'card' || b === 'bank')
  )
  const hasCard = present.has('card')
  const hasBank = present.has('bank')

  const labelKey = (hasCard && hasBank) ? 'checkout.payWithCardOrBank'
    : hasBank ? 'checkout.payWithBank'
    : 'checkout.payWithCard'

  const brandSubtitle = methods
    .filter(m => {
      const b = PAYMENT_METHOD_DISPLAY[m]?.bucket
      return b === 'card' || b === 'bank'
    })
    .map(m => PAYMENT_METHOD_DISPLAY[m].brand)
    .join(' · ')

  return { labelKey, brandSubtitle }
}

/**
 * Build the ordered list of payment buckets for the delta flow.
 *
 * Returns descriptors consumed by PaymentMethodSelector + PayFlow:
 *   { id, kind: 'stripe'|'paypal', methodTypes?, allowCardFallback?, labelKey, brandSubtitle? }
 *
 * @param {object}   opts
 * @param {string[]} opts.paymentMethodTypes   merchant config (Stripe methods)
 * @param {string}   opts.currency             booking currency
 * @param {boolean}  opts.needsOffSession      first payment that saves a method off-session
 * @param {boolean}  opts.paypalDirectEnabled  merchant has direct PayPal
 * @returns {Array<object>}
 */
export function getPaymentBuckets({ paymentMethodTypes = [], currency, needsOffSession = false, paypalDirectEnabled = false }) {
  const cur = (currency || '').toLowerCase()
  const known = (paymentMethodTypes || []).filter(m => PAYMENT_METHOD_DISPLAY[m])
  const buckets = []

  // stripe_main: card + bank methods (everything except mobilepay). In an
  // off-session context, drop methods that can't be saved off-session (e.g.
  // pay_by_bank) so the Payment Element never requests them.
  let mainMethods = known.filter(m => PAYMENT_METHOD_DISPLAY[m].bucket !== 'mobilepay')
  if (needsOffSession) mainMethods = mainMethods.filter(m => OFF_SESSION_CAPABLE.has(m))

  buckets.push({
    id: 'stripe_main',
    kind: 'stripe',
    methodTypes: mainMethods,
    allowCardFallback: true,
    ...getCardOptionLabel(mainMethods),
  })

  // mobilepay: own lazy bucket — only when offered, currency-compatible, and
  // on-session (it can't be saved off-session).
  if (known.includes('mobilepay') && MOBILEPAY_CURRENCIES.includes(cur) && !needsOffSession) {
    buckets.push({
      id: 'mobilepay',
      kind: 'stripe',
      methodTypes: ['mobilepay'],
      allowCardFallback: false,
      labelKey: 'checkout.payWithMobilePay',
    })
  }

  // paypal: direct (non-Stripe) integration. Hidden in an off-session context —
  // PayPal can't be saved off-session, so a deposit/off-session first payment
  // must go through a card-capable method instead (matches the booking app).
  if (paypalDirectEnabled && !needsOffSession) {
    buckets.push({
      id: 'paypal',
      kind: 'paypal',
      labelKey: 'checkout.payWithPayPal',
    })
  }

  return buckets
}
